import React, { Component } from 'react'
import { Table, Button, Icon, Spin, Tooltip } from 'antd'
import { files } from '@services/api'

interface InvoiceItem {
    id: number
    number: string
    created_at: string | null
    total_charge_amount: number
    retrieval_key: string | null
    action: string
    // The cache's attributed owner, or null when the document has no cached transaction.
    // Both are null whenever the cache was not consulted -- see CACHE_CONSULTED.
    cache_owner_user_id: number | null
    // Non-null only when that owner is somebody other than the client on screen.
    cache_owner_name: string | null
}

interface Props {
    userId: number | string
    userIsCollective?: string
}

interface State {
    data: InvoiceItem[]
    loading: boolean
    blockingError: string | null
    notice: string | null
    fallbackStatus: string | null
}

const FALLBACK_HEADER = 'x-tranzila-invoice-fallback'
const CACHE_SERVED_NOTICE = 'טרנזילה אינה זמינה כרגע; מוצגות חשבוניות מהרישומים שלנו, ייתכן שחסרה חשבונית מהיום.'
const EMAIL_SERVED_NOTICE =
    'לא ניתן היה לבדוק את היסטוריית החשבוניות מהרישומים שלנו; מוצגות רק חשבוניות שהתקבלו מטרנזילה.'
// The fallback statuses in which a cache read actually happened. Outside these the
// ownership fields are null because nothing was compared -- an unavailable cache must
// never be rendered as "no backing transaction".
const CACHE_CONSULTED = [
    'email_plus_cache',
    'cache_found',
    'email_only',
    'cache_empty',
    'email_failed_cache_served',
    'queue_overflow_cache_served'
]
// The API's own request queue to Tranzila was full (the request was not sent): not an outage of Tranzila.
const QUEUE_OVERFLOW_TEXT = 'תור הבקשות לטרנזילה מלא כרגע. נסו שוב מאוחר יותר.'

const FALLBACK_NOTICES: { [status: string]: string } = {
    rate_limited: CACHE_SERVED_NOTICE,
    unavailable: CACHE_SERVED_NOTICE,
    failed: CACHE_SERVED_NOTICE,
    email_failed_cache_served: CACHE_SERVED_NOTICE,
    queue_overflow: QUEUE_OVERFLOW_TEXT,
    queue_overflow_cache_served: QUEUE_OVERFLOW_TEXT,
    cache_failed_email_served: EMAIL_SERVED_NOTICE
}

export default class InvoicesTab extends Component<Props, State> {
    constructor(props: Props) {
        super(props)
        this.state = {
            data: [],
            loading: true,
            blockingError: null,
            notice: null,
            fallbackStatus: null
        }
    }

    fallbackStatusFromHeaders(headers: any) {
        const value = headers && headers[FALLBACK_HEADER]
        return value ? String(value) : null
    }

    fallbackError(status: string | null) {
        if (status === 'rate_limited') return 'לא ניתן לבדוק חשבוניות כרגע בגלל עומס זמני בטרנזילה. נסו שוב מאוחר יותר.'
        if (status === 'unavailable') return 'טרנזילה אינה זמינה כרגע, ולכן לא ניתן היה לבדוק חשבוניות.'
        if (status === 'failed') return 'בדיקת החשבוניות החלופית נכשלה. לא ניתן לקבוע אם קיימים מסמכים.'
        if (status === 'email_failed_cache_served') {
            return 'טרנזילה אינה זמינה כרגע ולא נמצאו חשבוניות שמורות להצגה.'
        }
        if (status === 'queue_overflow' || status === 'queue_overflow_cache_served') return QUEUE_OVERFLOW_TEXT
        if (status === 'cache_failed_email_served') {
            return 'לא ניתן היה לבדוק את היסטוריית החשבוניות מהרישומים שלנו ולא התקבלו חשבוניות להצגה.'
        }
        return null
    }

    fallbackNotice(status: string | null) {
        return status ? FALLBACK_NOTICES[status] || null : null
    }

    componentDidMount() {
        const { userId, userIsCollective } = this.props

        files
            .getClientInvoices({ userId, userIsCollective: !!userIsCollective })
            .then(res => {
                const fallbackStatus = this.fallbackStatusFromHeaders(res.headers)
                if (!Array.isArray(res.data)) {
                    this.setState({
                        data: [],
                        loading: false,
                        blockingError: 'אירעה שגיאה בטעינת החשבוניות',
                        notice: null,
                        fallbackStatus
                    })
                    return
                }

                const data = res.data.map((invoice: any) => ({
                    id: invoice.id,
                    number: invoice.number,
                    created_at: invoice.created_at,
                    total_charge_amount: invoice.total_charge_amount,
                    retrieval_key: invoice.retrieval_key,
                    action: invoice.action,
                    cache_owner_user_id:
                        invoice.cache_owner_user_id === null || invoice.cache_owner_user_id === undefined
                            ? null
                            : Number(invoice.cache_owner_user_id),
                    cache_owner_name: invoice.cache_owner_name || null
                }))
                const fallbackError = this.fallbackError(fallbackStatus)
                this.setState({
                    data,
                    loading: false,
                    blockingError: data.length ? null : fallbackError,
                    notice: data.length ? this.fallbackNotice(fallbackStatus) : null,
                    fallbackStatus
                })
            })
            .catch(err => {
                console.error('Failed to fetch invoices:', err)
                const fallbackStatus = this.fallbackStatusFromHeaders(err && err.response && err.response.headers)
                const isServerError = err && err.response && err.response.status === 500
                this.setState({
                    loading: false,
                    blockingError:
                        this.fallbackError(fallbackStatus) ||
                        (isServerError ? 'שגיאת שרת. נסו שוב מאוחר יותר' : 'אירעה שגיאה בטעינת החשבוניות'),
                    notice: null,
                    fallbackStatus
                })
            })
    }

    formatDate(dateStr: string | null) {
        if (!dateStr) return ''
        const date = new Date(dateStr)
        if (Number.isNaN(date.getTime())) return ''
        return (
            date.toLocaleDateString('he-IL') +
            ' - ' +
            date.toLocaleTimeString('he-IL', {
                hour: '2-digit',
                minute: '2-digit'
            })
        )
    }

    handleDownload = (retrievalKey: string) => {
        files
            .getTranzilaInvoice({ retrieval_key: retrievalKey })
            .then(res => {
                const blob = new Blob([res.data], { type: 'application/pdf' })
                const url = window.URL.createObjectURL(blob)
                window.open(url, '_blank')
            })
            .catch(err => {
                console.error('Download failed:', err)
                alert('שגיאה בהורדת החשבונית. נסו שוב מאוחר יותר.')
            })
    }

    render() {
        const { data, loading, blockingError, notice, fallbackStatus } = this.state
        const isNonProduction = fallbackStatus === 'skipped_non_production'
        const cacheConsulted = !!fallbackStatus && CACHE_CONSULTED.indexOf(fallbackStatus) !== -1

        const columns = [
            {
                title: '',
                key: 'icon',
                width: 72,
                align: 'center',
                render: (_: any, record: InvoiceItem) => (
                    <span>
                        <Icon type="file-text" style={{ fontSize: 18, color: '#d32029' }} />
                        {record.cache_owner_name ? (
                            <Tooltip title={`חשבונית של לקוח מקושר: ${record.cache_owner_name}`}>
                                <Icon
                                    type="info-circle"
                                    theme="filled"
                                    style={{ fontSize: 14, color: '#1890ff', marginRight: 6 }}
                                />
                            </Tooltip>
                        ) : null}
                    </span>
                )
            },
            {
                title: 'מספר חשבונית',
                dataIndex: 'number',
                key: 'number',
                align: 'center'
            },
            {
                title: 'תאריך הפקת חשבונית',
                dataIndex: 'created_at',
                key: 'created_at',
                align: 'center',
                render: (text: string | null) => this.formatDate(text)
            },
            {
                title: 'סכום',
                dataIndex: 'total_charge_amount',
                key: 'amount',
                align: 'center',
                render: (_: any, record: InvoiceItem) => {
                    return (
                        <div>
                            <strong>₪{record.total_charge_amount}</strong>
                            {record.action === '3' ? (
                                <strong style={{ color: 'green', marginRight: '5px' }}>
                                    {/* Split only when the cache was actually read. "Absent from
                                        the cache" means NO LINKED CACHED TRANSACTION -- not
                                        "raised manually": it also covers a transaction dropped by
                                        the storage predicate (tranmode 'N' carrying a txnfdid, an
                                        unquantified path) and a voided document. A census of
                                        unreferenced ids puts the upper bound near 14,189
                                        documents (1.42%), so this appears more often than the
                                        first two known examples suggest. */}
                                    {cacheConsulted && record.cache_owner_user_id === null
                                        ? 'זיכוי חשבונית מס'
                                        : 'זיכוי'}
                                </strong>
                            ) : (
                                ''
                            )}
                        </div>
                    )
                }
            },
            {
                title: 'הורדה',
                key: 'download',
                align: 'center',
                render: (_: any, record: InvoiceItem) => (
                    <Button
                        type="link"
                        icon="download"
                        disabled={!record.retrieval_key}
                        onClick={() => record.retrieval_key && this.handleDownload(record.retrieval_key)}
                    />
                )
            }
        ]

        return (
            <div style={{ direction: 'rtl' }}>
                {loading ? (
                    <div style={{ textAlign: 'center', padding: 40 }}>
                        <Icon type="loading" style={{ fontSize: 24 }} spin />
                    </div>
                ) : blockingError ? (
                    <div style={{ color: 'red', textAlign: 'center', padding: 20 }}>{blockingError}</div>
                ) : (
                    <React.Fragment>
                        {notice ? (
                            <div
                                style={{
                                    background: '#fff7e6',
                                    border: '1px solid #ffd591',
                                    color: '#ad4e00',
                                    marginBottom: 16,
                                    padding: 12
                                }}
                            >
                                {notice}
                            </div>
                        ) : null}
                        {isNonProduction ? (
                            <div style={{ color: '#666', marginBottom: 12 }}>
                                חיפוש חלופי לפי מזהה לקוח אינו פעיל בסביבה זו.
                            </div>
                        ) : null}
                        {!isNonProduction || data.length > 0 ? (
                            <Table
                                rowKey="id"
                                dataSource={data}
                                columns={columns}
                                pagination={false}
                                locale={{ emptyText: 'אין חשבוניות להצגה' }}
                            />
                        ) : null}
                    </React.Fragment>
                )}
            </div>
        )
    }
}
